import { SkeletonLoader } from '@toggle/design-system';
import { Entity, ThemeExposure, ThemeSchemaResponse } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ThemeHeaderDetailed } from '~/components/theme-header-detailed/ThemeHeaderDetailed';
import { useThemeCompanies } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { ThemePerformanceSection } from '~/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';

import { StepperSection } from '../components/stepper-section/StepperSection';
import { TopCompaniesSection } from './components/top-companies-section/TopCompaniesSection';
import * as S from './ThemeOverviewTab.styles';

const TOP_COMPANIES_LIMIT = 10;

interface ThemeOverviewTabProps {
  entity?: Entity;
  theme: ThemeExposure | { theme: ThemeSchemaResponse };
  title?: string;
  isPerfDrawer?: boolean;
}

export const ThemeOverviewTab = ({
  entity,
  theme,
  title,
  isPerfDrawer = false,
}: ThemeOverviewTabProps) => {
  const {
    data = [],
    isFetching,
    error,
  } = useThemeCompanies({ themeId: theme.theme.id });

  if (isFetching) {
    return <SkeletonLoader areas={S.loadingSkeletonAreas} />;
  }

  if (error) {
    throw error;
  }

  const entityTags = data
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const slicedData = data.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(0, data.length - TOP_COMPANIES_LIMIT);

  return (
    <S.Root>
      {isPerfDrawer ? (
        <ThemeHeaderDetailed theme={theme.theme} title={title} />
      ) : (
        <StepperSection
          entity={entity}
          theme={theme}
          title={title}
          activeIndex={1}
          description={theme.theme.description}
        />
      )}
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          entityTags={entityTags}
          themeId={theme.theme.id}
        />
      </ErrorBoundaryWrapper>
      {!!slicedData.length && (
        <>
          <S.HorizontalDivider />
          <TopCompaniesSection
            companies={slicedData}
            remainingCount={remainingCount}
          />
        </>
      )}
    </S.Root>
  );
};
